// templatesPadrao.ts
//
// Os templates de mensagem que a aba "Alertas e Notificações" mostra enquanto a
// organização não tem nenhum gravado.
//
// Por que saíram do JSX: os três primeiros estavam escritos como literal DENTRO
// do `return` de AlertsAndNotifications.tsx. Literal em JSX não é dado — não dá
// para gravar, comparar com o banco, nem abrir para edição sem que o "editado"
// seja jogado fora no próximo render. Aqui eles viram SEMENTE: o que a tela
// exibe, marcado como padrão do sistema, até existir linha em
// `acd."ActOrganizationInfo"` do tipo 20.
//
// Os dois últimos são NOVOS, e o motivo é o buraco da ordem de compra: o
// template "Pedido Fornecedor" que existia manda quantidade, produto, loja,
// estoque_atual e ponto_reposicao — e nada mais. Não tem código de ordem, não
// tem preço, não tem valor total, não tem uma linha por item. Com ele não se
// envia uma ordem de compra a fornecedor nenhum; envia-se um aviso de estoque
// baixo. Ordem de compra precisa de cabeçalho (código, fornecedor, loja, data,
// total) e de uma linha por item (produto, quantidade, unidade, preço unitário,
// subtotal) — que são exatamente os dois templates acrescentados no fim.

/**
 * 20 em `organizationInfoType`.
 *
 * O enum do backend (`src/common/organization.enum.ts`) vai só até 8 (sku,
 * phoneNumber, email, description, website, socialMedia, businessHours,
 * foundedYear) — todos do legado. A partir de 20 é NOSSO, a mesma convenção já
 * escrita e provada em `subsidiaryInfo.enum.ts` ("a faixa baixa é do legado e
 * significa coisas diferentes em acd e aod"). Escolher 9 encostaria na faixa que
 * o legado ainda pode ocupar.
 */
export const ORGANIZATION_INFO_TEMPLATE = 20;

export type CanalDeTemplate = "whatsapp" | "email" | "sms";

export interface TemplateDeMensagem {
  /** uuid da linha em ActOrganizationInfo. Ausente = ainda não foi gravado. */
  organizationInfoUuid?: string;
  nome: string;
  canal: CanalDeTemplate;
  conteudo: string;
  /** true quando é semente deste arquivo, não linha vinda do banco. */
  padraoDoSistema?: boolean;
}

export const ROTULO_CANAL: Record<CanalDeTemplate, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  sms: "SMS",
};

/**
 * As variáveis de um template são DERIVADAS do texto, nunca digitadas à parte.
 *
 * Manter as duas coisas separadas é o defeito do dado antigo: o template de
 * validade listava `dias_restantes` como variável e o texto usava
 * `{dias_restantes}` — igual por sorte. Na primeira edição do texto as duas
 * listas divergem e o envio passa a deixar `{...}` cru na mensagem do cliente.
 */
export function extrairVariaveis(conteudo: string): string[] {
  const encontradas = (conteudo || "").match(/\{([a-zA-Z0-9_]+)\}/g) || [];
  const semChaves = encontradas.map((bruto) => bruto.slice(1, -1));
  // Sem duplicata e na ordem em que aparecem no texto — é assim que quem lê o
  // template confere a lista contra a mensagem.
  return semChaves.filter((nome, indice) => semChaves.indexOf(nome) === indice);
}

export const TEMPLATES_PADRAO: TemplateDeMensagem[] = [
  {
    nome: "Template Estoque Baixo",
    canal: "whatsapp",
    conteudo:
      "🚨 ALERTA: Estoque de {produto} abaixo do limite na {loja}. Estoque atual: {quantidade}. Ponto de reposição: {ponto_reposicao}",
    padraoDoSistema: true,
  },
  {
    nome: "Template Pedido Fornecedor",
    canal: "email",
    conteudo:
      "Prezados,\n\nSolicitamos o envio de {quantidade} unidades de {produto} para {loja}.\n\nEstoque atual: {estoque_atual}\nPonto de reposição: {ponto_reposicao}\n\nAtenciosamente,\nSistema de Gestão",
    padraoDoSistema: true,
  },
  {
    nome: "Template Validade Próxima",
    canal: "whatsapp",
    conteudo:
      "⚠️ AVISO: Produto {produto} próximo do vencimento na {loja}. Data de validade: {validade}. Dias restantes: {dias_restantes}",
    padraoDoSistema: true,
  },
  // Os dois templates da ORDEM DE COMPRA, agora com o que a §10.5 exige e que
  // faltava: `{itens}` (o bloco montado pelo servidor, uma linha por item),
  // `{n_itens}`, `{endereco_entrega}`, `{prazo}` e `{contato}` no cabeçalho, e
  // `{codigo}` na linha do item. Sem endereço e prazo, o fornecedor recebe um
  // pedido que ele não sabe onde nem quando entregar.
  //
  // `{itens}` substituiu o parágrafo "(uma linha por item, ver o template ...)":
  // aquele texto era uma instrução para quem LÊ o template e ia sair na
  // mensagem do fornecedor. O servidor troca `{itens}` pelas linhas renderizadas.
  //
  // ESTE TEXTO É O MESMO do backend
  // (`src/services/suprimentos/compras/mensagem.service.ts`). Duas sementes
  // diferentes fariam a prévia desta tela mentir sobre o que sairia de verdade.
  {
    nome: "Ordem de Compra — cabeçalho e rodapé",
    canal: "email",
    conteudo:
      "Prezados {fornecedor},\n\nSegue a ordem de compra {codigo_ordem}, emitida em {data} para a unidade {loja}.\n\n--- ITENS ---\n\n{itens}\n\n--- FIM DOS ITENS ---\n\nValor total da ordem: {valor_total}   ({n_itens} itens)\n\nEntregar em: {loja}, {endereco_entrega}\nPrazo: {prazo}\nContato: {contato}\n\nConfirme o recebimento desta ordem respondendo a esta mensagem com o número {codigo_ordem}.\n\nAtenciosamente,\n{loja}",
    padraoDoSistema: true,
  },
  {
    nome: "Ordem de Compra — linha do item",
    canal: "email",
    conteudo:
      "{quantidade} {unidade} de {produto} ({codigo}) — {preco_unitario}/{unidade} = {subtotal_linha}",
    padraoDoSistema: true,
  },
];

/**
 * Templates GRAVADOS da organização, lidos do infoToken.
 *
 * A travessia é `organization.organizationInfo`, a mesma que DynamicTable usa
 * para os SKUs (tipo 1) — não existe `decryptedInfoToken.organizationInfo` no
 * primeiro nível. Valor malformado não pode branquear a aba: a linha quebrada
 * é pulada e as outras aparecem.
 */
export function lerTemplatesGravados(
  decryptedInfoToken: any
): TemplateDeMensagem[] {
  const linhas: any[] = decryptedInfoToken?.organization?.organizationInfo ?? [];

  return linhas
    .filter(
      (info: any) =>
        Number(info?.organizationInfoType) === ORGANIZATION_INFO_TEMPLATE
    )
    .map((info: any): TemplateDeMensagem | null => {
      let valor: any = {};
      try {
        valor = JSON.parse(info.organizationInfoValue || "{}");
      } catch {
        return null;
      }
      const canal: CanalDeTemplate =
        valor?.canal === "email" || valor?.canal === "sms"
          ? valor.canal
          : "whatsapp";
      return {
        organizationInfoUuid: info.organizationInfoUuid,
        // `organizationInfoName` é a coluna que guarda o nome; o JSON repete o
        // nome para o modal não depender da coluna ao editar.
        nome: valor?.nome || info.organizationInfoName || "(sem nome)",
        canal,
        conteudo: typeof valor?.conteudo === "string" ? valor.conteudo : "",
      };
    })
    .filter((template): template is TemplateDeMensagem => template !== null);
}
